import { app_url } from "app_config"
import Breadcrumb from "components/Common/Breadcrumb"
import React, { useEffect, useMemo, useState } from "react"
import { Link, useNavigate, useParams } from "react-router-dom"
import { Container, CardBody, Card, Col, Row } from "reactstrap"
import CreateClient from "./CreateClient"
import ServiceBilling from "./ServiceBilling"
import Personal from "./Personal"
import TaxReferences from "./TaxReferences"
import BookeepingManagement from "./BookeepingManagement"
import ClientContextBox from "../ClientContextBox"
import ClientNotes from "./ClientNotes"
import PracticeResponsibility from "./PracticeResponsibility"
import ClientInvoices from "./ClientInvoices"
import { usePermissionData } from "AppContext"
import PermittedComponent from "components/Common/PermittedComponent"
import KYCSection from "./KYCSection"
import ClientTasks from "./ClientTasks"
import DirectorsAndPSC from "./DirectorsAndPSC"

const AddClient = () => {
  const [tab_index, setTabIndex] = useState(0)
  const [fixedSearch, setFixedSearch] = useState(false)
  const [clientName, setClientName] = useState(false)

  const [clientCreated, setClientCreated] = useState(false)
  const [cType, setCType] = useState(1)

  const [isChanged, setIsChanged] = useState({
    0: false, // client details
    1: false, // personal
    2: false, // service & billing
    3: false, // tax reference
    4: false, // Bookkeeping
    5: false, // Practice responsibility
  })

  const [hasPractice, setHasPractice] = useState(true)

  const { checkAccess } = usePermissionData()
  const module_id = 1

  const { client_id } = useParams()

  // fixed sidetab
  useEffect(() => {
    const handleScroll = () => setFixedSearch(window.scrollY > 50)
    window.addEventListener("scroll", handleScroll)

    return () => {
      window.removeEventListener("scroll", handleScroll)
    }
  }, [])

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search)
    // Extract the 'name' query parameter
    const tabIndex = queryParams.get("tab")

    if (client_id && tabIndex) {
      setTabIndex(Number(tabIndex - 1))
    }
  }, [])

  document.title = `${
    client_id ? "Edit" : "Add"
  } Client | Peacock Accountants Limited`

  const changeStep = (manualSwitch = null) => {
    if (manualSwitch) {
      setTabIndex(manualSwitch)
      setTimeout(() => window.scrollTo(0, 0), 100)
    } else if (tab_index < tabs.length - 1) {
      setTabIndex(prev => prev + 1)
      setTimeout(() => window.scrollTo(0, 0), 100)
    }
  }

  // tabs data
  const tabs = useMemo(
    () => [
      {
        name: "Client Details",
        Component: (
          <CreateClient
            changeStep={changeStep}
            client_id={client_id}
            setClientCreated={setClientCreated}
            setHasPractice={setHasPractice}
          />
        ),
      },
      {
        name: "Personal Details",
        Component: <Personal changeStep={changeStep} client_id={client_id} />,
        isPersonal: true,
      },
      {
        name: "Services & Billing",
        Component: (
          <ServiceBilling changeStep={changeStep} client_id={client_id} />
        ),
      },
      {
        name: "Tax References",
        Component: (
          <TaxReferences changeStep={changeStep} client_id={client_id} />
        ),
      },
      {
        name: "Bookeeping Management",
        Component: (
          <BookeepingManagement changeStep={changeStep} client_id={client_id} />
        ),
      },
      {
        name: "Practice Responsibility",
        Component: (
          <PracticeResponsibility
            changeStep={changeStep}
            client_id={client_id}
            setHasPractice={setHasPractice}
          />
        ),
      },
      // {
      //   name: "AML/KYC",
      //   Component: <KYCSection changeStep={changeStep} client_id={client_id} />
      // },
      {
        name: "Directors & PSC's",
        Component: <DirectorsAndPSC />,
      },
      ...(client_id
        ? [
            // {
            //   name: "Xero Invoices",
            //   Component: <ClientInvoices client_id={client_id} />,
            // },
            ...(checkAccess(2, "view")
              ? [
                  {
                    name: "Tasks",
                    Component: <ClientTasks client_id={client_id} />,
                  },
                ]
              : []),
            {
              name: "Client Notes",
              Component: (
                <ClientNotes
                  client_id={client_id}
                  heading={"Client Note"}
                  type={1}
                />
              ),
            },
            {
              name: "Work Notes",
              Component: (
                <div>
                  <ClientNotes
                    client_id={client_id}
                    heading={"Work Note"}
                    type={2}
                  />
                </div>
              ),
            },
          ]
        : []),
    ],
    []
  )

  const handlePreventReload = e => e.preventDefault()

  function passClientName(name) {
    setClientName(name)
  }

  function onChangeTab(index) {
    if (!hasPractice) {
      swal(
        "",
        "Please fill up the practice responsibility to proceed ahead",
        "warning"
      )
      return
    }

    if (isChanged[tab_index] && tab_index === 5) {
      swal(
        "",
        "Please fill up the practice responsibility to proceed ahead",
        "warning"
      )
      return
    }

    if (isChanged[tab_index] && index != tab_index && client_id) {
      swal({
        title: "Unsaved Changes !",
        text: 'Save your changes before switching tabs. To proceed without saving, click " Go ahead ".',
        icon: "warning",
        buttons: true,
        dangerMode: true,
        buttons: {
          cancel: "Back",
          confirm: {
            text: "Go ahead",
          },
        },
      }).then(willDelete => {
        if (willDelete) {
          setTabIndex(index)
        }
      })
    } else setTabIndex(index)
  }

  const CURRENT_COMPONENT = tabs.find(
    (_, index) => index === tab_index
  )?.Component

  return (
    <div>
      <div className="page-content mb-5">
        <Container fluid>
          <Breadcrumb
            title={"Dashboard"}
            breadcrumbItem={`${client_id ? "Edit" : "Add"} Client 
                    ${client_id && clientName ? ` (${clientName})` : ""}`}
          />
          <div>
            <PermittedComponent
              isAccess={checkAccess(
                module_id,
                client_id
                  ? checkAccess(module_id, "edit")
                    ? "edit"
                    : "view"
                  : "add"
              )}
            >
              <Row className="align-items-start">
                <Col md={2} className={`pe-1`}>
                  <Card>
                    <CardBody
                      className={`${
                        fixedSearch && "fixed_sidebar"
                      } rounded custom_side_nav p-2`}
                    >
                      {tabs.map((tab, index) => {
                        return (
                          <div
                            key={tab.name}
                            className="mb-2 position-relative"
                          >
                            <button
                              disabled={
                                !clientCreated && !client_id
                                // ||
                                // (index !== 5 && !hasPractice)
                              }
                              onClick={() => onChangeTab(index)}
                              className={`side_navlink text-capitalize  
                                ${index === tab_index && "bg-white fw-bold"}
                                 ${tab.isPersonal && cType === 1 && "d-none"}`}
                              title={isChanged[index] ? "Unsaved changes" : ""}
                            >
                              {tab.name}
                            </button>
                            {isChanged[index] && client_id && (
                              <div className="fas fa-circle unsaved_icon"></div>
                            )}
                          </div>
                        )
                      })}
                    </CardBody>
                  </Card>
                </Col>
                <Col md={10}>
                  <div>
                    <ClientContextBox
                      client_id={client_id}
                      passClientName={passClientName}
                      isChanged={isChanged}
                      setIsChanged={setIsChanged}
                      changeStep={changeStep}
                      setHasPractice={setHasPractice}
                      setCType={setCType}
                    >
                      <form onSubmit={handlePreventReload}>
                        {CURRENT_COMPONENT}
                      </form>
                    </ClientContextBox>
                  </div>
                </Col>
              </Row>
            </PermittedComponent>
          </div>
        </Container>
      </div>
    </div>
  )
}

export default AddClient
